import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { toast } from 'sonner'
import * as performanceAPI from '@/api/performance'
import { okrKeys } from './feedbackKeys'
import type { OKRProgressAnalytics } from '@/types/performance'

// ================================
// QUERIES
// ================================

/**
 * Use OKR Progress Query
 *
 * Fetches OKR progress analytics for the current tenant.
 * Cached for 2 minutes - OKRs change less frequently than feedbacks.
 *
 * @example
 * ```tsx
 * const { data: okrData, isLoading, isFetching } = useOKRProgress()
 * // okrData = { okrs: [...], overallProgress: 75.2, ... }
 * ```
 */
export function useOKRProgress() {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: okrKeys.progress(tenant?.domain || ''),
    queryFn: async (): Promise<OKRProgressAnalytics> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      return await performanceAPI.getOKRProgress(tenant.domain)
    },
    enabled: !!tenant?.domain,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Use All Individual OKRs Query (Admin Listing)
 *
 * Fetches all individual OKR evaluations across the organization.
 * Used by the admin "Objetivos Individuais" page.
 */
export function useAllIndividualOKRs(params?: {
  status?: string
  search?: string
  limit?: number
}) {
  const { tenantDomain } = useTenant()

  return useQuery({
    queryKey: okrKeys.individualAll(tenantDomain || '', params),
    queryFn: async () => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await performanceAPI.getAllIndividualOKRs(tenantDomain, { ...params })
    },
    enabled: !!tenantDomain,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

// ================================
// UTILITY HOOKS
// ================================

/**
 * Invalidate OKR Cache
 *
 * Manually invalidate OKR cache to force refetch.
 * Useful for manual refresh buttons.
 *
 * @example
 * ```tsx
 * const invalidateOKRs = useInvalidateOKRCache()
 *
 * <Button onClick={() => invalidateOKRs()}>
 *   Refresh
 * </Button>
 * ```
 */
export function useInvalidateOKRCache() {
  const { tenant } = useTenant()
  const queryClient = useQueryClient()

  return () => {
    if (!tenant?.domain) return

    queryClient.invalidateQueries({
      queryKey: okrKeys.all,
    })
  }
}
